import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import cx from "classnames"
import { toast } from "react-toastify"
import format from "date-fns/format"
import { toZonedTime } from "date-fns-tz"
import {
    PiPlusBold,
    PiThumbsUpDuotone,
    PiHandPalmDuotone,
    PiArrowCounterClockwiseBold,
} from "react-icons/pi"

import People from "../People"
import Button from "../Button"
import Tip from "../Tip"
import AutoSaveField from "../AutoSaveField"
import RemoveControl from "../RemoveControl"
import { CheckBox } from "../CheckBoxes"
import apiSignOff from "../../services/signOff/signOff.api"
import { getOrgSignOffs } from "../../services/api/v2/signOff"
import { paginateAll } from "../../services/api/utils"
import { getObjectId, getRefId } from "../../utils/utils"
import { SIGN_OFF_STATUS, SIGN_OFF_STATUS_LABEL, isSignOffOpen, signOffAbilities } from "../../utils/signOffs"
import "./SignOffs.scss"

const when = (date) => (date ? format(toZonedTime(new Date(date)), "eee. MMM d, yyyy h:mma") : "")

/** The status chip. */
// The app's pill colours (dna-pill-box + colour); cancelled stays a plain pill
const STATUS_PILL = {
    [SIGN_OFF_STATUS.REQUESTED]: "orange-pill",
    [SIGN_OFF_STATUS.READY]: "blue-pill",
    [SIGN_OFF_STATUS.APPROVED]: "green-pill",
    [SIGN_OFF_STATUS.REJECTED]: "red-pill",
}

// Ready is the normal state of a sign-off: no pill for it
export const SignOffStatus = ({ signOff }) =>
    !signOff?.status || signOff.status === SIGN_OFF_STATUS.READY ? null : (
        <span className={cx("dna-pill-box", STATUS_PILL[signOff.status])}>
            {SIGN_OFF_STATUS_LABEL[signOff.status] || signOff.status}
        </span>
    )

/**
 * The person record the avatars show for a ref id: the org's, else the
 * project's, else a stub with the name the sign-off kept.
 */
function personFor(id, name, orgData, mission) {
    const match = (list) => (list || []).find((p) => getRefId(p) === id)
    const found = match(orgData?.people) || match(mission?.people)

    if (found) return found.ref && typeof found.ref === "object" ? { ...found.ref, ...found, _id: id } : found

    return id ? { _id: id, firstName: name || "Person", lastName: "" } : null
}

/**
 * Reject needs a note: a small inline form, no second dialog.
 */
const RejectForm = ({ onCancel, onSubmit, busy }) => {
    const [note, setNote] = useState("")

    return (
        <div>
            <textarea
                autoFocus
                placeholder="Why is it rejected, and what has to change?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
            />
            <div className="x-sign-off-actions">
                <Button className={cx("red", { disabled: busy || !note.trim() })} onClick={() => onSubmit(note.trim())}>
                    Reject
                </Button>
                <Button className={cx("transparent", { disabled: busy })} onClick={onCancel}>
                    Never mind
                </Button>
            </div>
        </div>
    )
}

/**
 * One sign-off, as the task's Sign-offs tab shows it. Everything goes
 * through the sign-off endpoints; the row re-renders from the socket.
 */
export const SignOffRow = ({ signOff, app, orgData, mission, ctx, people, onChanged }) => {
    const [busy, setBusy] = useState(false)
    const [rejecting, setRejecting] = useState(false)
    const orgId = getObjectId(signOff.org)
    const can = signOffAbilities(signOff, ctx)
    const open = isSignOffOpen(signOff)
    const approver = personFor(getRefId(signOff.approver?.ref), signOff.approver?.name, orgData, mission)
    const requesterName = signOff.requestedBy?.name || "Somebody"
    const meIsApprover = can.decide || can.withdraw

    const run = async (fn, done) => {
        if (busy) return

        setBusy(true)

        try {
            const res = await fn()

            if (res) onChanged?.(res)
            if (done) done(res)
        } catch (err) {
            toast.error(err?.message || "Sorry, that didn't work. Please try again.")
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className={cx("x-sign-off-row", `is-${signOff.status}`)}>
            <div className="x-sign-off-main">
                <People
                    org={orgData}
                    app={app}
                    round
                    noFlip
                    addMessage={approver ? "Change approver" : "Assign to an approver"}
                    addMessagePosition="bottom"
                    addButton={can.edit}
                    closeOnSelect
                    position="absolute"
                    orgData={orgData}
                    dontAutoAssign
                    data={approver ? [approver] : []}
                    searchList={people}
                    onAdd={(r) => run(() => apiSignOff.update(orgId, signOff._id, { approverId: getRefId(r) }))}
                    onRemove={
                        can.edit ? () => run(() => apiSignOff.update(orgId, signOff._id, { approverId: null })) : null
                    }
                    adderTitle="Who signs this off?"
                    adderMsg={<p>Anybody in the organisation. They need not be on the project.</p>}
                />

                <div className="x-sign-off-text">
                    {can.edit ? (
                        <AutoSaveField
                            className="w-300"
                            value={signOff.description || ""}
                            placeHolder="Short one-line description..."
                            onSave={(value) => run(() => apiSignOff.update(orgId, signOff._id, { description: value }))}
                        />
                    ) : (
                        <span>{signOff.description || <span className="dna-light-text">No description</span>}</span>
                    )}
                </div>

                {can.edit && signOff.approver?.ref && open && (
                    <Tip
                        title={
                            signOff.status === SIGN_OFF_STATUS.REJECTED
                                ? "Resubmit it for approval"
                                : "Tell the approver it is ready"
                        }
                    >
                        <div className="x-sign-off-ready">
                            <CheckBox
                                controlled
                                checked={signOff.status === SIGN_OFF_STATUS.READY}
                                onChange={(a, b, checked) =>
                                    run(() => apiSignOff.update(orgId, signOff._id, { ready: !!checked }))
                                }
                            />
                            {signOff.status === SIGN_OFF_STATUS.REJECTED ? "Resubmit" : "Ready for approval"}
                        </div>
                    </Tip>
                )}

                <SignOffStatus signOff={signOff} />
            </div>

            {signOff.note &&
                (signOff.status === SIGN_OFF_STATUS.REJECTED || signOff.status === SIGN_OFF_STATUS.APPROVED) && (
                    <div className="x-sign-off-note">
                        <strong>{signOff.status === SIGN_OFF_STATUS.REJECTED ? "Rejected: " : "Note: "}</strong>
                        {signOff.note}
                    </div>
                )}

            {meIsApprover && !rejecting && (
                <div className="x-sign-off-actions">
                    {can.decide && (
                        <>
                            <Button
                                mint
                                icon={true}
                                className={cx({ disabled: busy })}
                                onClick={(e) =>
                                    run(
                                        () => apiSignOff.decide(orgId, signOff._id, { decision: "approved" }),
                                        () => app.doneBurst?.tune({ x: e.pageX, y: e.pageY }).generate().replay(),
                                    )
                                }
                            >
                                <PiThumbsUpDuotone /> Approve
                            </Button>
                            <Button
                                icon={true}
                                clear
                                className={cx("transparent", { disabled: busy })}
                                onClick={() => setRejecting(true)}
                            >
                                <PiHandPalmDuotone /> Reject
                            </Button>
                        </>
                    )}
                    {can.withdraw && (
                        <Button
                            icon={true}
                            danger

                            className={cx("transparent", { disabled: busy })}
                            onClick={() =>
                                app.confirm({
                                    comp: <p>Take your approval back? The request goes back to ready for approval.</p>,
                                    onYes: () => run(() => apiSignOff.withdraw(orgId, signOff._id)),
                                })
                            }
                        >
                            <PiArrowCounterClockwiseBold /> Withdraw approval
                        </Button>
                    )}
                </div>
            )}

            {rejecting && (
                <RejectForm
                    busy={busy}
                    onCancel={() => setRejecting(false)}
                    onSubmit={(note) =>
                        run(
                            () => apiSignOff.decide(orgId, signOff._id, { decision: "rejected", note }),
                            () => setRejecting(false),
                        )
                    }
                />
            )}

            <div className="x-sign-off-meta dna-light-text dna-smaller-text">
                <span>
                    Requested by {requesterName} {signOff.createdAt && <>on {when(signOff.createdAt)}</>}
                </span>
                {signOff.decidedAt && signOff.status !== SIGN_OFF_STATUS.READY && (
                    <span>
                        · {SIGN_OFF_STATUS_LABEL[signOff.status]} by {signOff.decidedBy?.name || "the approver"} on{" "}
                        {when(signOff.decidedAt)}
                    </span>
                )}
                {can.cancel && (
                    <RemoveControl
                        title="Cancel this request"
                        onClick={() =>
                            app.confirm({
                                severe: true,
                                yesText: "Cancel request",
                                comp: <p>Cancel this sign-off request? The approver is told.</p>,
                                onYes: () => run(() => apiSignOff.cancel(orgId, signOff._id)),
                            })
                        }
                    />
                )}
            </div>
        </div>
    )
}

/**
 * The sign-offs of one thing (a task, a project): the list, the add button
 * and the cancelled ones folded away. Reads the collection and follows its
 * socket changes, so the mobile app's and mAX's writes show up too.
 *
 * @param type {String} SIGN_OFF_TYPE.*
 * @param refId {String} the thing's id
 * @param mission {Object} the project it is on, when any (for the people and the rules)
 * @param canRequest {Boolean} whether this person may ask for one here
 */
const SignOffs = ({ app, orgData, type, refId, mission, canRequest, limit = 100 }) => {
    const [list, setList] = useState(null)
    const [adding, setAdding] = useState(false)
    const orgId = getObjectId(orgData?._id || mission?.org)
    const personId = app.state.person?._id
    const mounted = useRef(true)

    const ctx = useMemo(
        () => ({
            personId,
            orgUser: (orgData?.people || []).find((p) => getRefId(p) === personId),
            missionUser: (mission?.people || []).find((p) => getRefId(p) === personId),
        }),
        [personId, orgData, mission],
    )

    const people = useMemo(() => {
        const seen = new Set()
        const out = []

        ;[...(orgData?.people || []), ...(mission?.people || [])].forEach((p) => {
            const id = getRefId(p)

            if (!id || seen.has(id) || p.permission === 3 || p.isArchived) return

            seen.add(id)
            out.push(personFor(id, null, orgData, mission))
        })

        return out
    }, [orgData, mission])

    const load = useCallback(async () => {
        if (!orgId || !refId) return

        try {
            const rows = await paginateAll(
                getOrgSignOffs(orgId, {
                    type,
                    refId,
                    ...(mission?._id ? { mission: getObjectId(mission._id) } : {}),
                    status: ["requested", "ready", "approved", "rejected", "cancelled"],
                    sortBy: "updatedAt",
                    sortDirection: 1,
                    limit: Math.min(limit, 100),
                }),
                { limit },
            )

            if (mounted.current) setList(rows)
        } catch (err) {
            if (mounted.current) setList([])
        }
    }, [orgId, refId, type, mission?._id, limit])

    useEffect(() => {
        mounted.current = true
        load()

        return () => {
            mounted.current = false
        }
    }, [load])

    useEffect(() => {
        const apply = (changes) => {
            changes.forEach((change) => {
                const doc = change?.doc

                if (!doc || String(doc.refId) !== String(refId)) return

                setList((current) => {
                    const rows = current || []
                    const index = rows.findIndex((row) => row._id === doc._id)

                    if (index === -1) return [...rows, doc]

                    const next = rows.slice()
                    next[index] = { ...rows[index], ...doc }

                    return next
                })
            })
        }
        const created = apiSignOff.onChanges("create", apply)
        const updated = apiSignOff.onChanges("update", apply)

        return () => {
            created.disconnect()
            updated.disconnect()
        }
    }, [refId])

    const onChanged = (doc) => {
        if (!doc?._id) return

        setList((current) => {
            const rows = current || []
            const index = rows.findIndex((row) => row._id === doc._id)

            if (index === -1) return [...rows, doc]

            const next = rows.slice()
            next[index] = { ...rows[index], ...doc }

            return next
        })
    }

    const add = async (e) => {
        if (adding) return

        setAdding(true)
        app.circleBurst?.(e.pageX, e.pageY)

        try {
            const created = await apiSignOff.request(orgId, { type, refId })

            onChanged(created)
        } catch (err) {
            toast.error(err?.message || "Sorry, that didn't work. Please try again.")
        } finally {
            setAdding(false)
        }
    }

    const rows = list || []
    const live = rows.filter((so) => so.status !== SIGN_OFF_STATUS.CANCELLED)
    const cancelled = rows.filter((so) => so.status === SIGN_OFF_STATUS.CANCELLED)

    return (
        <div className="x-sign-offs">
            {list === null && <p>Loading...</p>}

            {list !== null && !live.length && !canRequest && <p>No sign-offs yet.</p>}

            {live.map((so) => (
                <SignOffRow
                    key={so._id}
                    signOff={so}
                    app={app}
                    orgData={orgData}
                    mission={mission}
                    ctx={ctx}
                    people={people}
                    onChanged={onChanged}
                />
            ))}

            {canRequest && (
                <div className="x-sign-off-add">
                    <Tip title="Ask somebody to sign this off">
                        <Button className={cx("x-aix-rnd-nb", { disabled: adding })} onClick={add}>
                            <PiPlusBold />
                        </Button>
                    </Tip>
                </div>
            )}

            {cancelled.length > 0 && (
                <Tip
                    html={
                        <>
                            {cancelled.map((so) => (
                                <div className="tip-left" key={so._id}>
                                    <p>{so.description || "No description was given"}</p>
                                    <p>
                                        Cancelled by {so.cancelledBy?.name || "somebody"}{" "}
                                        {so.cancelledAt && <>on {when(so.cancelledAt)}</>}
                                    </p>
                                </div>
                            ))}
                        </>
                    }
                    className="dna-smaller-text dna-light-text cursor"
                    style={{ display: "inline-block", marginTop: 18 }}
                >
                    {cancelled.length} Cancelled
                </Tip>
            )}
        </div>
    )
}

export default SignOffs
